import React, { useMemo, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AreaUnitsProvider } from '$contexts/area-units';
import { Carousel, CarouselWrapper, CarouselPagination } from '$partials/base/carousel.jsx';
import { CuratedArticleCarouselBlock, GetInTouchBlock, PropertyDetailSection } from '$partials/modules/blocks';
import { ExchangeRateProvider, useExchangeRate } from '$contexts/exchange-rate';
import { Footer, Header } from '$partials/global';
import { formatCurrency, formatPropertyId, groupPropertyModulesStream } from '$utilities/data';
import { PropertyInfoCard, PropertySchema } from '$partials/properties/index.js';
import { PropertyProvider } from '$contexts/property';
import IconArrow from '$partials/icons/icon-arrow';
import IconGallery from '$partials/icons/icon-gallery.jsx';
import MediaGallery, { MediaGalleryProvider, MediaGalleryContext } from '$partials/global/media-gallery.jsx';
import ModuleDispatcher from '$partials/modules/dispatcher.jsx';
import PropertyCardCarousel from '$partials/modules/blocks/property-card-carousel';
import PropertyDetailMapDialog from '$partials/properties/property-detail-map-dialog';
import SimpleImage from '$partials/media/image-simple.jsx';
import TextAccordion from '$partials/modules/blocks/text-accordion.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').SalePropertyDetailPage}
 */
const SalePropertyDetailPage = (props) => {
    const page = props.data.page;
    const { currency, convertToCurrency } = useExchangeRate() || {};

    const carouselRef = useRef(null);
    const [cursorPos, setCursorPos] = useState({ x: 0,
        y: 0,
        visible: false });
    const [showMap, setShowMap] = useState(false);
    const [showAllModules, setShowAllModules] = useState(page.modules?.length > 0 ? false : true);

    const handleMouseMove = (e) => {
        const rect = carouselRef.current?.getBoundingClientRect();
        if (!rect) { return; }
        // position the 40x40 box so its bottom-right corner is at the cursor
        const x = e.clientX - rect.left - 40;
        const y = e.clientY - rect.top - 40;
        setCursorPos({ x,
            y,
            visible: true });
    };

    const handleMouseLeave = () => setCursorPos((s) => ({ ...s,
        visible: false }));

    const modules = useMemo(() => groupPropertyModulesStream(page.modules), [page?.modules]);
    const mediaItems = useMemo(() => page.modules?.filter(item => item.__typename === 'PropertyDetailSectionMediaItem'), [page?.modules]);
    const propertyReferenceId = formatPropertyId(page.externalId, page.parent?.externalReferenceId);

    return (
        <AreaUnitsProvider defaultUnit={page?.market?.areaUnit === 'sqm' || page?.market?.areaUnit === 'sqft' ? page.market.areaUnit : undefined}>
            <PropertySchema
                isRental={false}
                page={page}
                url={props?.location} />

            <PropertyProvider property={{
                externalId: page.externalId,
                office: page.office,
                primaryAgent: page.primaryAgent,
                propertyReferenceId: propertyReferenceId,
                pageTitle: page.title,
                pageUrl: props?.location
            }}>
                <Header
                    {...props.data.header}
                    variant='PDP' />

                <main
                    id='main-content'>
                    {/* Property details */}
                    <MediaGalleryProvider>
                        <MediaGalleryContext.Consumer>
                            {({ setGalleryInitialId, galleryInitialId }) => (
                                <>
                                    <div
                                        className='relative cursor-pointer aspect-[375/210] md:aspect-[1440/600] max-h-[85svh] z-30'
                                        onMouseEnter={() =>
                                            setCursorPos((s) => ({ ...s,
                                                visible: true }))
                                        }
                                        onMouseLeave={handleMouseLeave}
                                        onMouseMove={handleMouseMove}
                                        ref={carouselRef}>
                                        {!showMap ? (
                                            <Carousel
                                                swiperConfig={{
                                                    a11y: { enabled: true },
                                                    slidesPerView: 1,
                                                    grabCursor: false,
                                                }}>
                                                <CarouselWrapper>
                                                    {page?.images?.map((item) => {
                                                        const video = item.media.video || item.media.videoEmbed;
                                                        const hasVideo = !!video?.src;
                                                        return (
                                                            <SwiperSlide
                                                                aria-roledescription='slide'
                                                                className='select-none cursor-pointer'
                                                                key={item.id}
                                                                onClick={() =>
                                                                    setGalleryInitialId(item.id)
                                                                }
                                                                role='group'>
                                                                {hasVideo ? (
                                                                    <SimpleImage
                                                                        className='w-full aspect-[375/210] md:aspect-[1440/600] max-h-[85svh] object-cover'
                                                                        src={video.poster.url}/>
                                                                ) : (
                                                                    <SimpleImage
                                                                        className='w-full aspect-[375/210] md:aspect-[1440/600] max-h-[85svh] object-cover'
                                                                        {...item.media?.image}/>
                                                                )}
                                                            </SwiperSlide>
                                                        );
                                                    })}
                                                </CarouselWrapper>

                                                {page?.images?.length > 1 && (
                                                    <CarouselPagination totalSlides={page.images.length} />
                                                )}
                                            </Carousel>
                                        ) : (
                                            <>
                                                <PropertyDetailMapDialog
                                                    property={{
                                                        ...page,
                                                        title: page.title || '',
                                                        primaryAgent: null,
                                                        images: null,
                                                        documents: null,
                                                        externalId: null,
                                                        propertyReferenceId: formatPropertyId(page.externalId, page.parent?.externalReferenceId)
                                                    }}
                                                    setShowMap={setShowMap}
                                                    showMap={showMap}/>
                                            </>
                                        )}

                                        {!showMap && (
                                            <div
                                                aria-hidden
                                                className='absolute left-0 top-0 h-40 w-40 rounded bg-grey-grey-200 pointer-events-none flex items-center justify-center'
                                                style={{
                                                    transform: `translate(${cursorPos.x}px, ${cursorPos.y}px)`,
                                                    transition: 'transform 0.04s linear, opacity 0.08s linear',
                                                    opacity: cursorPos.visible ? 1 : 0,
                                                    zIndex: 40,
                                                }}>
                                                <IconGallery fill='#2c2b2aff' />
                                            </div>
                                        )}
                                    </div>

                                    {galleryInitialId && page?.images?.length > 0 && (
                                        <MediaGallery
                                            initialId={galleryInitialId}
                                            items={page.images}/>
                                    )}
                                </>
                            )}
                        </MediaGalleryContext.Consumer>

                        <ExchangeRateProvider
                            defaultCurrency={page.priceCurrency}
                            exchangeRate={props.data?.exchangeRates}>
                            <PropertyInfoCard
                                {...page}
                                propertyReferenceId={propertyReferenceId}
                                setShowMap={setShowMap}
                                title={page.title || ''} />
                        </ExchangeRateProvider>
                    </MediaGalleryProvider>

                    {(page?.highlights?.length > 0 || page?.amenities?.length > 0) && (
                        <section>
                            <TextAccordion
                                col1Heading='Highlights'
                                col1Items={page?.highlights}
                                col2Heading='Amenities'
                                col2Items={page?.amenities}
                                heading='Key Details'/>
                        </section>
                    )}

                    <MediaGalleryProvider>
                        <div
                            className={`${showAllModules ? '' : 'max-h-[550px] md:max-h-[700px] overflow-y-clip relative'}`}>

                            {!showAllModules && (
                                <>
                                    <div
                                        className='absolute inset-0 z-10 h-full w-full'
                                        style={{
                                            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 82.22%)'
                                        }} />
                                    <div className='absolute z-20 inset-x-0 bottom-0 pb-32 w-full flex justify-center'>
                                        <button
                                            aria-label='Open additional content sections'
                                            className='type-style-eyebrow inline-flex gap-8 items-center'
                                            onClick={() => setShowAllModules(true)}
                                            type='button'>
                                            {'See more'}
                                            <span aria-hidden={true}>
                                                <IconArrow className='rotate-90 h-10' />
                                            </span>
                                        </button>
                                    </div>
                                </>
                            )}

                            <div inert={!showAllModules}>
                                {modules?.length > 0 && (
                                    <section
                                        className='theme-light-alt'>
                                        {modules.map((module, i) => (
                                            <ModuleDispatcher
                                                {...module}
                                                key={i + module?.id}/>
                                        ))}
                                    </section>
                                )}
                                {page.area?.images?.length > 0 && (
                                    <>
                                        <div className='layout-container layout-site-outer-padding my-48 md:my-64'>
                                            <hr className='border-t border-main-border' />
                                        </div>
                                        <section className='theme-light-alt'>
                                            <PropertyDetailSection
                                                body={page.area.body}
                                                heading={page.area.heading}
                                                images={page.area.images} />
                                        </section>
                                    </>
                                )}
                                {(() => {
                                    const additionalItems = [
                                        {
                                            value: page?.epcRating,
                                            label: 'EPC Rating'
                                        },
                                        {
                                            value: page?.councilTaxBand,
                                            label: 'Council Tax Band'
                                        },
                                        {
                                            value: page?.councilTaxAuthority,
                                            label: 'Local Authority'
                                        },
                                        {
                                            value: page?.tenure?.name,
                                            label: 'Tenure'
                                        },
                                        ...(page.tenure?.name === 'Leasehold' ? [{
                                            value: !isNaN(page.tenureExpiryYear) && `${page.tenureExpiryYear - new Date().getFullYear()} years left`,
                                            label: 'Length of lease'
                                        }] : []),
                                        ...(page.tenure?.name === 'Leasehold' ? [{
                                            value: !isNaN(page.serviceChargePerAnnum) && formatCurrency(convertToCurrency({
                                                from: page.priceCurrency || 'GBP',
                                                to: currency,
                                                amount: page.serviceChargePerAnnum }), currency || page.priceCurrency || 'GBP') + ' annually',
                                            label: 'Service charge'
                                        }] : []),
                                        ...(page.tenure?.name === 'Leasehold' ? [{
                                            value: !isNaN(page.groundRentPerAnnum) && formatCurrency(convertToCurrency({
                                                from: page.priceCurrency || 'GBP',
                                                to: currency,
                                                amount: page.groundRentPerAnnum
                                            }), currency || page.priceCurrency || 'GBP') + ' annually',
                                            label: 'Ground rent'
                                        }] : []),
                                    ];

                                    const hasData = additionalItems.some(item => item.value);

                                    if (!hasData || (page.parent?.__typename === 'SalePropertyListingPage' && page.parent.displayAdditionalSection === false)) {
                                        return null;
                                    }

                                    return (
                                        <>
                                            <div className='layout-container layout-site-outer-padding my-48 md:my-64'>
                                                <hr className='border-t border-main-border' />
                                            </div>
                                            <section>
                                                <TextAccordion
                                                    col1Heading='Additional'
                                                    col1Items={additionalItems.map(item => ({text: `${item.label}: ${item.value ?? 'Not available'}`}))}/>
                                            </section>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                        <section className='theme-light-alt'>
                            <GetInTouchBlock />
                        </section>
                        <section>
                            <PropertyCardCarousel
                                heading='Similar Properties for Sale'
                                items={[{
                                    items: page?.similarProperties,
                                    listing: {
                                        url: page?.parent?.url
                                    }}]}/>
                        </section>
                        <section>
                            <CuratedArticleCarouselBlock
                                featuredArticle={page?.relatedArticles?.[0]?.featuredArticle}
                                heading={page?.relatedArticles?.[0]?.heading}
                                items={page?.relatedArticles?.[0]?.items} />
                        </section>
                        <MediaGalleryContext.Consumer>
                            {({ galleryInitialId }) => {
                                return (
                                    galleryInitialId && mediaItems?.length > 0 && (
                                        <MediaGallery
                                            initialId={galleryInitialId}
                                            items={mediaItems} />
                                    )
                                );
                            }}
                        </MediaGalleryContext.Consumer>
                    </MediaGalleryProvider>
                </main>

                <Footer {...props.data.footer} />
            </PropertyProvider>
        </AreaUnitsProvider>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default SalePropertyDetailPage;
